import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Building2, Users, Receipt, HardDrive, Crown, Trash2, Eye, Power, PowerOff,
  FolderKanban, TrendingUp, Zap
} from 'lucide-react';

function formatSize(bytes) {
  if (bytes < 1024) return '0 B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  return (bytes / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
}

const PLAN_COLORS = {
  starter: 'text-slate-400 bg-slate-400/10 border-slate-400/20',
  professional: 'text-amber-400 bg-amber-400/10 border-amber-400/20',
  enterprise: 'text-violet-400 bg-violet-400/10 border-violet-400/20',
};

export default function SuperAdmin() {
  const { API } = useAuth();
  const [stats, setStats] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [showUsers, setShowUsers] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [tenantUsers, setTenantUsers] = useState([]);
  const [showConfirmDelete, setShowConfirmDelete] = useState(null);

  useEffect(() => {
    Promise.all([axios.get(`${API}/admin/stats`), axios.get(`${API}/admin/tenants`)])
      .then(([s, t]) => { setStats(s.data); setTenants(t.data); })
      .catch(() => toast.error('Zugriff verweigert'));
  }, [API]);

  const handlePlanChange = async (tid, plan) => {
    await axios.put(`${API}/admin/tenants/${tid}`, { plan });
    setTenants(prev => prev.map(t => t.id === tid ? { ...t, plan } : t));
    toast.success('Plan geändert');
  };

  const handleToggleActive = async (tid, active) => {
    await axios.put(`${API}/admin/tenants/${tid}`, { active });
    setTenants(prev => prev.map(t => t.id === tid ? { ...t, active } : t));
    toast.success(active ? 'Tenant aktiviert' : 'Tenant deaktiviert');
  };

  const handleDelete = async (tid) => {
    try {
      await axios.delete(`${API}/admin/tenants/${tid}`);
      setTenants(prev => prev.filter(t => t.id !== tid));
      setShowConfirmDelete(null);
      toast.success('Tenant gelöscht');
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
  };

  const openUsers = async (tenant) => {
    setSelectedTenant(tenant);
    const res = await axios.get(`${API}/admin/tenants/${tenant.id}/users`);
    setTenantUsers(res.data);
    setShowUsers(true);
  };

  const kpis = stats ? [
    { label: 'Tenants', value: stats.total_tenants, sub: `${stats.active_tenants} aktiv`, icon: Building2, color: 'text-blue-400 bg-blue-400/10' },
    { label: 'Benutzer', value: stats.total_users, sub: 'gesamt', icon: Users, color: 'text-emerald-400 bg-emerald-400/10' },
    { label: 'Dokumente', value: stats.total_documents, sub: 'Finanzdokumente', icon: Receipt, color: 'text-amber-400 bg-amber-400/10' },
    { label: 'Umsatz', value: `${stats.total_revenue.toLocaleString('de-DE')} EUR`, sub: 'Rechnungen gesamt', icon: TrendingUp, color: 'text-rose-400 bg-rose-400/10' },
    { label: 'Speicher', value: formatSize(stats.total_storage), sub: 'belegt', icon: HardDrive, color: 'text-violet-400 bg-violet-400/10' },
  ] : [];

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200" data-testid="superadmin-page">
      {/* Header */}
      <div className="bg-slate-900/80 border-b border-slate-800 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-violet-500 rounded-sm flex items-center justify-center">
            <Crown className="w-5 h-5 text-white" strokeWidth={2} />
          </div>
          <div>
            <h1 className="text-lg font-bold text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Mont<span className="text-amber-500">ARO</span> <span className="text-violet-400 text-sm font-normal ml-1">Super Admin</span>
            </h1>
            <p className="text-xs text-slate-500">Plattform-Verwaltung</p>
          </div>
        </div>
        {stats && (
          <div className="flex items-center gap-4">
            {Object.entries(stats.plan_distribution || {}).map(([plan, count]) => (
              <div key={plan} className="flex items-center gap-1.5">
                <Badge className={`${PLAN_COLORS[plan]} rounded-sm text-xs capitalize`}>{plan}</Badge>
                <span className="text-sm font-mono text-slate-400">{count}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6 space-y-6">
        {/* KPIs */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {kpis.map(k => (
              <div key={k.label} className="bg-slate-900/50 border border-slate-800/60 rounded-sm p-5">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs text-slate-500 uppercase tracking-wider">{k.label}</span>
                  <div className={`w-8 h-8 rounded-sm flex items-center justify-center ${k.color}`}>
                    <k.icon className="w-4 h-4" strokeWidth={1.5} />
                  </div>
                </div>
                <p className="text-2xl font-bold text-white font-mono">{k.value}</p>
                <p className="text-xs text-slate-500 mt-1">{k.sub}</p>
              </div>
            ))}
          </div>
        )}

        {/* Tenants Table */}
        <div className="bg-slate-900/50 border border-slate-800/60 rounded-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-800/40 flex items-center justify-between">
            <h2 className="text-sm font-bold text-white uppercase tracking-wider">Alle Mandanten</h2>
            <span className="text-xs font-mono text-slate-500">{tenants.length} Tenants</span>
          </div>
          <table className="w-full text-left">
            <thead>
              <tr className="bg-slate-900/80 border-b border-slate-800/40">
                <th className="px-4 py-3 text-xs font-mono uppercase text-slate-500 tracking-wider">Firma</th>
                <th className="px-4 py-3 text-xs font-mono uppercase text-slate-500 tracking-wider">Plan</th>
                <th className="px-4 py-3 text-xs font-mono uppercase text-slate-500 tracking-wider text-right">User</th>
                <th className="px-4 py-3 text-xs font-mono uppercase text-slate-500 tracking-wider text-right">Projekte</th>
                <th className="px-4 py-3 text-xs font-mono uppercase text-slate-500 tracking-wider text-right">Rechnungen</th>
                <th className="px-4 py-3 text-xs font-mono uppercase text-slate-500 tracking-wider text-right">Umsatz</th>
                <th className="px-4 py-3 text-xs font-mono uppercase text-slate-500 tracking-wider text-right">Speicher</th>
                <th className="px-4 py-3 text-xs font-mono uppercase text-slate-500 tracking-wider">Status</th>
                <th className="px-4 py-3 text-xs font-mono uppercase text-slate-500 tracking-wider text-right">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {tenants.map(t => (
                <tr key={t.id} className="border-b border-slate-800/40 hover:bg-slate-900/80 transition-colors" data-testid={`tenant-${t.id}`}>
                  <td className="px-4 py-3">
                    <p className="text-sm font-medium text-white">{t.name}</p>
                    <p className="text-[10px] font-mono text-slate-600 truncate max-w-[150px]">{t.id}</p>
                  </td>
                  <td className="px-4 py-3">
                    <Select value={t.plan || 'starter'} onValueChange={v => handlePlanChange(t.id, v)}>
                      <SelectTrigger className="w-[130px] h-8 bg-slate-950 border-slate-800 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-800">
                        <SelectItem value="starter" className="text-slate-200">Starter</SelectItem>
                        <SelectItem value="professional" className="text-slate-200">Professional</SelectItem>
                        <SelectItem value="enterprise" className="text-slate-200">Enterprise</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-4 py-3 text-sm font-mono text-slate-300 text-right">
                    {t.user_count} <span className="text-slate-600">/ {t.limits?.max_users}</span>
                  </td>
                  <td className="px-4 py-3 text-sm font-mono text-slate-300 text-right">{t.project_count}</td>
                  <td className="px-4 py-3 text-sm font-mono text-slate-300 text-right">{t.invoice_count}</td>
                  <td className="px-4 py-3 text-sm font-mono text-amber-400 text-right">{t.revenue?.toLocaleString('de-DE', { minimumFractionDigits: 0 })} EUR</td>
                  <td className="px-4 py-3 text-sm font-mono text-slate-400 text-right">{formatSize(t.storage_used || 0)}</td>
                  <td className="px-4 py-3">
                    {t.active !== false ? (
                      <Badge className="bg-emerald-400/10 text-emerald-400 border-emerald-400/20 rounded-sm text-xs">Aktiv</Badge>
                    ) : (
                      <Badge className="bg-rose-400/10 text-rose-400 border-rose-400/20 rounded-sm text-xs">Deaktiviert</Badge>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1 justify-end">
                      <Button variant="ghost" size="icon" onClick={() => openUsers(t)}
                        className="text-slate-400 hover:text-blue-400 w-8 h-8" title="User anzeigen"><Users className="w-4 h-4" /></Button>
                      {t.active !== false ? (
                        <Button variant="ghost" size="icon" onClick={() => handleToggleActive(t.id, false)}
                          className="text-slate-400 hover:text-amber-400 w-8 h-8" title="Deaktivieren"><PowerOff className="w-4 h-4" /></Button>
                      ) : (
                        <Button variant="ghost" size="icon" onClick={() => handleToggleActive(t.id, true)}
                          className="text-slate-400 hover:text-emerald-400 w-8 h-8" title="Aktivieren"><Power className="w-4 h-4" /></Button>
                      )}
                      <Button variant="ghost" size="icon" onClick={() => setShowConfirmDelete(t)}
                        className="text-slate-400 hover:text-rose-400 w-8 h-8" title="Löschen"><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Tenant Users Dialog */}
      <Dialog open={showUsers} onOpenChange={setShowUsers}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-200 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-400" />
              Benutzer — {selectedTenant?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {tenantUsers.map(u => (
              <div key={u.id} className="flex items-center justify-between py-2 px-3 bg-slate-950/50 rounded-sm border border-slate-800/40">
                <div>
                  <p className="text-sm text-white">{u.name}</p>
                  <p className="text-xs text-slate-500">{u.email}</p>
                </div>
                <Badge className={`rounded-sm text-xs capitalize ${u.role === 'admin' ? 'text-rose-400 bg-rose-400/10' : u.role === 'projektleiter' ? 'text-blue-400 bg-blue-400/10' : 'text-emerald-400 bg-emerald-400/10'}`}>
                  {u.role}
                </Badge>
              </div>
            ))}
            {!tenantUsers.length && <p className="text-sm text-slate-500 text-center py-4">Keine Benutzer</p>}
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirm Delete Dialog */}
      <Dialog open={!!showConfirmDelete} onOpenChange={v => { if (!v) setShowConfirmDelete(null); }}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-200 max-w-sm">
          <DialogHeader><DialogTitle className="text-white">Tenant löschen?</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="bg-rose-500/10 border border-rose-500/20 rounded-sm p-3 text-sm text-rose-300">
              <p className="font-bold mb-1">Achtung — Unwiderruflich!</p>
              <p>Alle Daten von <span className="font-bold text-white">"{showConfirmDelete?.name}"</span> werden permanent gelöscht:</p>
              <p className="mt-1 text-xs">Benutzer, Projekte, Dokumente, Rechnungen, Einsätze, Einstellungen</p>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => setShowConfirmDelete(null)} className="flex-1 bg-slate-800 text-slate-200 hover:bg-slate-700 rounded-sm">Abbrechen</Button>
              <Button onClick={() => handleDelete(showConfirmDelete.id)} className="flex-1 bg-rose-500 text-white hover:bg-rose-400 rounded-sm font-bold">Endgültig löschen</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
